import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { FloatingNavigationModel } from '../../models/floating-navigation.model';
import { map, debounceTime } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';

export enum AtlasBreakpoints {
  XSmall = `(min-width: 0px) and (max-width: 374px)`,
  Small = `(min-width: 375px) and (max-width: 767px)`,
  Medium = `(min-width: 768px) and (max-width: 1023px)`,
  Large = `(min-width: 1024px) and (max-width: 1919px)`,
  XLarge = `(min-width: 1920px)`
}
@Injectable({
  providedIn: 'root'
})
export class FloatingNavigationService {

  navigations: any[];
  tabsChange: any[];
  elementsList: any;
  floatingNav: FloatingNavigationModel[];
  navMap: Map<string, FloatingNavigationModel>;

  // Right sidenav subjects
  private floatingNavigation$ = new Subject<Map<string, FloatingNavigationModel>>();
  private scrollHeightSubject = new Subject<number>();
  private viewHeight$ = new Subject<any>();
  private fragment: string;
  // Header height XL && LG | MD | SM && XS
  private _headerHeight: 98 | 82 | 130;

  constructor(
    private activatedRoute: ActivatedRoute,
    private _breakpointObserver: BreakpointObserver
  ) {
    this.navigations = [];
    this.tabsChange = [];
    this.floatingNav = [];

    this.navMap = new Map();

    // Fetch the fragment from the URL
    // This callbacks trigger before loading the component
    this.activatedRoute.fragment.subscribe((fragment: string) => {
      // Stores the fragment to scroll to section
      this.fragment = fragment;

      // Calling set timeout with 0 to differ the execution of setting scroll fragment to next CallStack execution
      setTimeout(() => {
        // Scroll to fragment after all sections are registered
        this.setScrollFragment(this.fragment);
      }, 0);
    });

    this._headerHeight = 98;
    this._registerBreakPointObserver();
  }

  public setNavItem(navItem: FloatingNavigationModel) {
    // Update Map
    // To set the first element position to 0(TOP)
    if (this.navMap.size === 0) {
      navItem.elementOffsetTop = 0;
    } else {
      navItem.elementOffsetTop = navItem.elementOffsetTop + this._headerHeight;
    }
    this.navMap.set(navItem.id, navItem);
    this.floatingNavigation$.next(this.navMap);
  }

  public getFloatingNavItems(): Observable<FloatingNavigationModel[]> {
    return this.floatingNavigation$
      .asObservable()
      .pipe(
        debounceTime(5),
        map((navItemMap) => {
          const navArray: FloatingNavigationModel[] = [];
          navItemMap.forEach(element => {
            navArray.push(element);
          });
          return navArray;
        })
      );
  }

  public clearNavItems(): void {
    this.navMap.clear();
    // Clear the previous fragment
    this.floatingNavigation$.next(this.navMap);
    this.fragment = null;
  }

  // View Change Detection
  public viewHeightChanged(isForced?: boolean): void {
    this.viewHeight$.next(isForced);
  }

  public isViewHeightChanged(): Observable<any> {
    return this.viewHeight$.asObservable();
  }

  /**
   * Scroll Interactions
   */
  public setScrollHeight(scrollHeight: number): void {
    this.scrollHeightSubject.next(scrollHeight);
  }

  /**
   * Scrolls to the section with given fragment
   * @param fragment: URL fragment
   */
  public setScrollFragment(fragment: string): void {
    if (fragment && this.navMap.size && this.navMap.get(fragment)) {
      this.scrollHeightSubject.next(this.navMap.get(fragment).elementOffsetTop);
    }
  }

  public getScrollHeight(): Observable<number> {
    return this.scrollHeightSubject.asObservable();
  }

  private _registerBreakPointObserver(): void {
    this._breakpointObserver.observe([
      AtlasBreakpoints.XLarge,
      AtlasBreakpoints.Large,
      AtlasBreakpoints.Medium,
      AtlasBreakpoints.Small,
      AtlasBreakpoints.XSmall
    ]).subscribe(result => {

      if (
        result.breakpoints[AtlasBreakpoints.XLarge]
        || result.breakpoints[AtlasBreakpoints.Large]
      ) {
        this._headerHeight = 98;
      } else if (result.breakpoints[AtlasBreakpoints.Medium]) {
        this._headerHeight = 82;
      } else {
        this._headerHeight = 130;
      }
    });
  }
}
